import { env } from "../../../config/env";
import { authStorage } from "../../../lib/storage/authStorage";
import { fetchFlyerCategoryOptions, serializeTemplateFields } from "../common/flyerTemplateDevService";
import type { FlyerCategoryOption, TemplateFieldDef } from "../common/flyerTemplateDevService";
import type { ItineraryTemplateItem } from "../common/templateTypes";

export { fetchFlyerCategoryOptions };
export type { FlyerCategoryOption, TemplateFieldDef };
export type { ItineraryTemplateItem };

/**
 * Init shape used by the itinerary dev form.
 * Mirrors ItineraryTemplateItem from the backend — all fields optional except id, name, category.
 */
export type ItineraryTemplateInit = Partial<ItineraryTemplateItem> & Pick<ItineraryTemplateItem, "id" | "name" | "category">;

function authHeaders(): HeadersInit {
    const token = authStorage.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

export interface CreateItineraryTemplateInput {
    category_slug: string;
    name: string;
    /** LLM content-generation instructions. Auto-extracted from sample_pdf if blank. */
    base_prompt: string;
    fields: TemplateFieldDef[];
    /** Preview thumbnail. Required unless sample_pdf is supplied (first page auto-used). */
    preview_image?: File;
    /** Sample itinerary PDF → LLM extracts base_prompt + fields from it. */
    sample_pdf?: File;
    /** Jinja2 .html layout file → deployed to templates/itinerary/ on the server. */
    html_template_file?: File;
    /** Template name without .html — auto-derived from filename if blank. */
    html_template_id?: string;
    /** Raw Jinja2 HTML pasted/edited directly — stored in Mongo as the primary render source. */
    html_template_content?: string;
    prompt_template?: string;
    tagline?: string;
    cta_text?: string;
}

export type UpdateItineraryTemplateInput = CreateItineraryTemplateInput;

export interface CreateItineraryTemplateResponse {
    message: string;
    template: ItineraryTemplateItem;
}

function buildFormData(input: CreateItineraryTemplateInput): FormData {
    const form = new FormData();
    form.append("category_slug", input.category_slug);
    form.append("name", input.name);
    form.append("base_prompt", input.base_prompt);
    form.append("fields_json", serializeTemplateFields(input.fields));
    if (input.preview_image) form.append("preview_image", input.preview_image);
    if (input.sample_pdf) form.append("sample_pdf", input.sample_pdf);
    if (input.html_template_file) form.append("html_template_file", input.html_template_file);
    form.append("html_template_id", input.html_template_id ?? "");
    form.append("html_template_content", input.html_template_content ?? "");
    form.append("prompt_template", input.prompt_template ?? "");
    form.append("tagline", input.tagline ?? "");
    form.append("cta_text", input.cta_text ?? "Book Now");
    return form;
}

export async function createItineraryTemplateDev(input: CreateItineraryTemplateInput): Promise<CreateItineraryTemplateResponse> {
    const form = buildFormData(input);
    const res = await fetch(`${env.apiBaseUrl}/rag/itinerary-templates`, {
        method: "POST",
        headers: authHeaders(),
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Create failed");
    }
    return res.json() as Promise<CreateItineraryTemplateResponse>;
}

export async function updateItineraryTemplateDev(
    templateId: string,
    input: UpdateItineraryTemplateInput,
): Promise<void> {
    const form = buildFormData(input);
    const res = await fetch(`${env.apiBaseUrl}/rag/itinerary-templates/${encodeURIComponent(templateId)}`, {
        method: "PUT",
        headers: authHeaders(),
        body: form,
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = (err as { detail?: string }).detail ?? res.statusText;
        throw new Error(typeof detail === "string" ? detail : "Update failed");
    }
}
